import * as R from "ramda";

// Abbreviates numbers compact version. EX: 1200000 -> $1.2M or 1200 -> $1.2K
// maximumFractionDigits: 2; minimumFractionDigits: 1
export const currencyFormatter = {
  format: (value: number, decimals = 2): string => {
    const formatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      notation: "compact",
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    return formatter.format(value).replace("K", "k");
  },
};

export const numberFormatter = {
  format: (value: number, decimals = 2): string => {
    // Abbreviates numbers compact version. EX: 1200000 -> 1.2M or 1200 -> 1.2K
    // maximumFractionDigits: 2; minimumFractionDigits: 1
    const intlNumberFormatter = new Intl.NumberFormat("en-US", {
      notation: "compact",
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    return intlNumberFormatter.format(value).replace("K", "k");
  },
};

export const snapShotDataSort = (data: any[], snapChartSortValue: string): any[] => {
  let snapData;

  switch (snapChartSortValue) {
    case "Alphabetical":
      snapData = R.sortBy(R.prop("name"), data);
      break;
    case "Highest to lowest":
    case "Spend: High to low":
      snapData = R.sortBy(R.compose(R.negate, R.prop("value")), data);
      break;
    case "Lowest to highest":
    case "Spend: Low to high":
      snapData = R.sortBy(R.prop("value"), data);
      break;
    case "Revenue: High to low":
    case "KPI Volume: High to low":
      snapData = R.sortBy(R.compose(R.negate, R.prop("value2")), data);
      break;
    case "Revenue: Low to high":
    case "KPI Volume: Low to high":
      snapData = R.sortBy(R.prop("value2"), data);
      break;
    case "Spend to effect ratio":
      snapData = R.sortBy(R.compose(R.negate, R.prop("spendOverOutcome")), data);
      break;
    default:
      snapData = data;
  }

  return snapData;
};

// Sorts spend and effect data, grouped first at the channel level, then platform, then tactic / brand-nonbrand
export const spendAndEffectDataSort = (data: any[], snapChartSortValue: string): any[] => {
  type ChannelName = string;
  type PlatformName = string;
  interface SortableProps {
    name: string;
    spendOverOutcome: number;
    value: number;
    value2: number;
  }
  interface SortedPlatforms extends SortableProps {
    names: SortableProps[];
  }
  interface SortedChannels extends SortableProps {
    platforms: Record<PlatformName, SortedPlatforms>;
  }

  const sortedData = R.clone(data);
  const channelGroups: Record<ChannelName, SortedChannels> = {};

  sortedData.forEach(item => {
    if (!channelGroups[item.channelName]) {
      channelGroups[item.channelName] = {
        name: item.channelName,
        spendOverOutcome: item.spendOverOutcome,
        value: item.value,
        value2: item.value2,
        platforms: {},
      };
    } else {
      let channelGroup = channelGroups[item.channelName];
      let mathFunc;
      switch (snapChartSortValue) {
        case "Alphabetical":
        case "Spend to effect ratio":
        case "Spend: High to low":
        case "Revenue: High to low":
        case "KPI Volume: High to low":
          mathFunc = Math.max;
          break;
        case "Spend: Low to high":
        case "Revenue: Low to high":
        case "KPI Volume: Low to high":
        default:
          mathFunc = Math.min;
      }
      channelGroup.spendOverOutcome = mathFunc(
        item.spendOverOutcome,
        channelGroup.spendOverOutcome
      );
      channelGroup.value = mathFunc(item.value, channelGroup.value);
      channelGroup.value2 = mathFunc(item.value2, channelGroup.value2);
    }

    if (!channelGroups[item.channelName].platforms[item.platformName]) {
      channelGroups[item.channelName].platforms[item.platformName] = {
        name: item.platformName,
        spendOverOutcome: item.spendOverOutcome,
        value: item.value,
        value2: item.value2,
        names: [
          {
            name: item.name,
            spendOverOutcome: item.spendOverOutcome,
            value: item.value,
            value2: item.value2,
          },
        ],
      };
    } else {
      let platformGroup = channelGroups[item.channelName].platforms[item.platformName];
      platformGroup.spendOverOutcome += item.spendOverOutcome;
      platformGroup.value += item.value;
      platformGroup.value2 += item.value2;
      platformGroup.names.push({
        name: item.name,
        spendOverOutcome: item.spendOverOutcome,
        value: item.value,
        value2: item.value2,
      });
    }
  });

  let sortMethod: (a: SortableProps) => R.Ord;
  switch (snapChartSortValue) {
    case "Spend to effect ratio":
      sortMethod = R.compose(R.negate, R.prop("spendOverOutcome"));
      break;
    case "Spend: High to low":
      sortMethod = R.compose(R.negate, R.prop("value"));
      break;
    case "Spend: Low to high":
      sortMethod = R.prop("value");
      break;
    case "Revenue: High to low":
    case "KPI Volume: High to low":
      sortMethod = R.compose(R.negate, R.prop("value2"));
      break;
    case "Revenue: Low to high":
    case "KPI Volume: Low to high":
      sortMethod = R.prop("value2");
      break;
    case "Alphabetical":
    default:
      sortMethod = R.prop("name");
  }

  // Convert channel and platform Records to arrays
  let sortedGroups = Object.values(channelGroups).map(channel => ({
    ...channel,
    platforms: Object.values(channel.platforms),
  }));

  sortedGroups.forEach(channel => {
    channel.platforms = R.sortBy(sortMethod, channel.platforms);
    channel.platforms.forEach(platform => {
      platform.names = R.sortBy(sortMethod, platform.names);
    });
  });
  sortedGroups = R.sortBy(sortMethod, sortedGroups);

  const flattenedSortedDataNames: string[] = R.flatten(
    sortedGroups.map(channel =>
      channel.platforms.map(platform => platform.names.map(name => name.name))
    )
  );

  sortedData.sort(
    (a, b) => flattenedSortedDataNames.indexOf(a.name) - flattenedSortedDataNames.indexOf(b.name)
  );

  return sortedData;
};

export const GROUP_BY_OPTIONS = [
  { label: "Channel", value: "channel" },
  { label: "Channel, Platform", value: "platform" },
  { label: "Channel, Platform, Tactic, Brand/Nonbrand", value: "name" },
];

const CHANNEL_NAME_MAP = {
  ctv: "CTV",
  olv: "OLV",
  fep: "FEP",
  pmax: "PMax",
  tiktok: "TikTok",
  youtube: "YouTube",
  ooh: "OOH",
  ott: "OTT",
};
// Turn display_television__total__none__none_spend into Display Television,  Total
export const capitalizeWords = (
  name: string,
  disabledPlatform?: boolean,
  useEnDash?: boolean
): string => {
  if (!name) {
    return "";
  }

  name = disabledPlatform ? name.replace(", Total", "").replace(", total", "") : name;

  let words = name
    .split(",")
    .map(item => item.replace(/_/g, " "))
    .filter(item => {
      return (
        item !== "none" &&
        item !== "spend" &&
        item !== "impressions" &&
        item !== "null" &&
        item !== " null" &&
        (disabledPlatform ? item.toLowerCase() !== "total" && item !== "" && item !== null : true)
      );
    });

  const capitalizeWord = (word: string): string => {
    const splitWord = word.split(" ");
    const capWords = splitWord.map(item => {
      if (CHANNEL_NAME_MAP[item.toLowerCase()]) {
        return CHANNEL_NAME_MAP[item.toLowerCase()];
      }
      return item.charAt(0).toUpperCase() + item.slice(1); // Capitalize the first letter of each word
    });

    const nonEmptyArray = capWords.filter(str => str !== "");
    return nonEmptyArray.join(" ");
  };
  const capitalizedWords: string[] = R.map(capitalizeWord, words);

  return useEnDash
    ? capitalizedWords.join(" – ").replace("Television", "TV").replace("Tv", "TV")
    : capitalizedWords.join(", ").replace("Television", "TV").replace("Tv", "TV"); // Join the words back together
};

export const CONVERSION_FORMATTER_TYPE = {
  conversion: numberFormatter,
  revenue: currencyFormatter,
};

export const getBarChartContributorsData = (data: any[], activeDependencies: Set<string>): any[] =>
  data.filter(channel => activeDependencies.has(channel.name));

export const TINUITI_TEST_HOME_SERVICES = "tinuititesthomeservices";
export const TINUITI_TEST_HOME_SERVICES_PROXY = "leafguard";
export const TINUITI_TEST_PROXY_KPI_VALUE = "Weekly_Shopify_Net_Revenue";
